import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Icon, type IconName } from '../primitives/Icon'

/**
 * Per-row overflow (⋮) menu for the HR Applications table.
 *
 * Mirrors the employee My-Leaves RowActionsMenu (outside-click + Escape close,
 * ↑↓ navigation, viewport-edge flip-up, subtle enter animation) so the two
 * leave tables behave identically. Which items show depends on leave status:
 *   • View    — always
 *   • Approve — pending / escalated
 *   • Reject  — pending / escalated
 *   • Cancel  — approved (gated by the caller via `canCancel`)
 */
export interface HrLeaveRowActionsProps {
  status: string
  /** Approved + flag on → show the destructive Cancel item. */
  canCancel: boolean
  onView: () => void
  onApprove: () => void
  onReject: () => void
  onCancel: () => void
}

export function HrLeaveRowActions({
  status,
  canCancel,
  onView,
  onApprove,
  onReject,
  onCancel,
}: HrLeaveRowActionsProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [flipUp, setFlipUp] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const decidable = status === 'pending' || status === 'escalated'
  // View + optional (approve, reject) + optional cancel.
  const itemCount = 1 + (decidable ? 2 : 0) + (canCancel ? 1 : 0)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  useEffect(() => {
    if (!open) return
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    setFlipUp(rect.bottom + (itemCount * 40 + 12) > window.innerHeight)
  }, [open, itemCount])

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const close = useCallback((): void => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        )
        if (items.length === 0) return
        const idx = items.findIndex((el) => el === document.activeElement)
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [close],
  )

  const run = (fn: () => void) => (): void => {
    close()
    fn()
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        data-testid="hr-leave-row-actions"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          background: open ? 'var(--surface-2)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text-2)',
          transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-2)'
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent'
        }}
      >
        <Icon name="more_vert" size={18} aria-hidden />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          data-testid="hr-leave-row-actions-menu"
          onKeyDown={handlePanelKeyDown}
          onClick={(event) => event.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            [flipUp ? 'bottom' : 'top']: 'calc(100% + 4px)',
            minWidth: 176,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-2)',
            zIndex: 100,
            overflow: 'hidden',
            padding: '6px 0',
            animation: 'lms-row-actions-in var(--motion-duration-fast) var(--motion-ease-out)',
          }}
        >
          <style>{`
            @keyframes lms-row-actions-in {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @media (prefers-reduced-motion: reduce) {
              @keyframes lms-row-actions-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
            }
          `}</style>

          <Item icon="visibility" label="View" testId="hr-leave-view" onClick={run(onView)} />
          {decidable ? (
            <>
              <Item icon="check" label="Approve" testId="hr-leave-approve" onClick={run(onApprove)} />
              <Item icon="close" label="Reject" testId="hr-leave-reject" destructive onClick={run(onReject)} />
            </>
          ) : null}
          {canCancel ? (
            <Item icon="close" label="Cancel leave" testId="hr-leave-cancel" destructive onClick={run(onCancel)} />
          ) : null}
        </div>
      )}
    </div>
  )
}

interface ItemProps {
  icon: IconName
  label: string
  onClick: () => void
  testId: string
  destructive?: boolean
}

function Item({ icon, label, onClick, testId, destructive = false }: Readonly<ItemProps>): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        background: hover
          ? destructive
            ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
            : 'var(--surface-2)'
          : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: destructive ? 'var(--danger)' : 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        outline: 'none',
      }}
    >
      <Icon name={icon} size={16} aria-hidden />
      {label}
    </button>
  )
}
